import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { CalendarClock, Plus, Wand2, Bell, CheckCircle, RotateCcw, Trash2, AlertTriangle, ArrowRight, CalendarPlus } from 'lucide-react';
import { BulkAttendeePicker } from './BulkAttendeePicker';

const STATUS_STYLE = {
  overdue: 'bg-red-50 text-red-700 border-red-200',
  planned: 'bg-slate-50 text-slate-600 border-slate-200',
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function TrainingPlanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const canManage = isAdmin || user?.app_roles?.hr_training === 'manager';
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [skills, setSkills] = useState([]);
  const [emps, setEmps] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [fps, setFps] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sched, setSched] = useState(null);
  const [savingSched, setSavingSched] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [p, s, sk, e, t] = await Promise.all([
        hrAPI.listPlan(params), hrAPI.planSummary(), hrAPI.listSkills(), hrAPI.listEmployees(), hrAPI.listTrainings(),
      ]);
      setRows(p.data || []); setSummary(s.data || null); setSkills(sk.data || []);
      setEmps(e.data || []); setTrainings(t.data || []);
      setSelected(new Set());
      if (fps.length === 0) { try { setFps((await hrAPI.listFunctionalPositions()).data || []); } catch { /* noop */ } }
    } catch { toast.error('Failed to load training plan'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const generate = async () => {
    try { const r = await hrAPI.generatePlanFromGaps(); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const runReminders = async () => {
    try { const r = await hrAPI.runPlanReminders(); toast.success(`Reminders processed — ${r.data.emails_sent ?? 0} email(s) sent`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const complete = async (r) => {
    const d = window.prompt(`Actual training date for ${r.employee_name} — ${r.skill_name} (YYYY-MM-DD):`, new Date().toISOString().slice(0, 10));
    if (!d) return;
    try { await hrAPI.completePlan(r.id, { actual_date: d }); toast.success('Marked complete'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const reopen = async (r) => {
    try { await hrAPI.reopenPlan(r.id); toast.success('Reopened'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete plan entry for ${r.employee_name} — ${r.skill_name}?`)) return;
    try { await hrAPI.deletePlan(r.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openNew = () => setDlg({ skill_id: '', employee_ids: [], planned_date: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10), training_id: '', notes: '' });
  const toggleEmp = (id) => setDlg(d => ({ ...d, employee_ids: d.employee_ids.includes(id) ? d.employee_ids.filter(x => x !== id) : [...d.employee_ids, id] }));
  const save = async () => {
    if (!dlg.skill_id) { toast.error('Select a skill'); return; }
    if (dlg.employee_ids.length === 0) { toast.error('Select at least one employee'); return; }
    if (!dlg.planned_date) { toast.error('Set a planned date'); return; }
    setSaving(true);
    try { const r = await hrAPI.createPlan(dlg); toast.success(r.data.message); setDlg(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const openSchedule = (r) => buildSched({ skill_id: r.skill_id, skill_label: r.skill_name, employee_ids: [r.employee_id], planned_date: r.planned_date, training_id: r.training_id });
  const buildSched = ({ skill_id, skill_label, employee_ids, planned_date, training_id }) => {
    const covering = skill_id ? trainings.filter(t => (t.skills_covered || []).includes(skill_id)) : [];
    const preTraining = training_id || (covering.length === 1 ? covering[0].id : '');
    setSched({ skill_id, skill_label, training_id: preTraining, planned_date: planned_date || new Date().toISOString().slice(0, 10), trainer_id: '', employee_ids });
  };
  const openGroupSchedule = () => {
    const chosen = rows.filter(r => selected.has(r.id));
    if (chosen.length === 0) return;
    const skillIds = [...new Set(chosen.map(r => r.skill_id))];
    const empIds = [...new Set(chosen.map(r => r.employee_id))];
    const single = skillIds.length === 1;
    buildSched({
      skill_id: single ? skillIds[0] : '',
      skill_label: single ? chosen[0].skill_name : `${skillIds.length} skills · ${empIds.length} employees`,
      employee_ids: empIds, planned_date: chosen[0].planned_date,
    });
  };
  const toggleSelect = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectableRows = rows.filter(r => r.status !== 'completed');
  const allSelected = selectableRows.length > 0 && selectableRows.every(r => selected.has(r.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(selectableRows.map(r => r.id)));
  const toggleSchedEmp = (id) => setSched(s => ({ ...s, employee_ids: s.employee_ids.includes(id) ? s.employee_ids.filter(x => x !== id) : [...s.employee_ids, id] }));
  const saveSchedule = async () => {
    if (!sched.training_id) { toast.error('Select a training'); return; }
    if (sched.employee_ids.length === 0) { toast.error('Add at least one attendee'); return; }
    setSavingSched(true);
    try {
      const r = await hrAPI.scheduleSessionFromPlan({ training_id: sched.training_id, planned_date: sched.planned_date, trainer_id: sched.trainer_id, employee_ids: sched.employee_ids });
      toast.success(r.data.message); setSched(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingSched(false); }
  };

  const Stat = ({ label, value, cls }) => (
    <div className={`rounded-lg border px-3 py-2 ${cls}`}><p className="text-lg font-bold leading-none">{value}</p><p className="text-[10px] uppercase tracking-wide mt-1">{label}</p></div>
  );

  return (
    <div className="space-y-5" data-testid="hr-training-plan-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><CalendarClock className="w-6 h-6 text-pink-600" /> Training Plan</h1>
          <p className="text-sm text-slate-500 mt-0.5">Auto-planned from skill gaps (within 1 month) + manually planned trainings, with planned-vs-actual tracking</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {selected.size > 0 && <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={openGroupSchedule} data-testid="hr-plan-group-schedule"><CalendarPlus className="w-4 h-4" /> Schedule Selected ({selected.size})</Button>}
            <Button size="sm" variant="outline" onClick={generate} className="gap-1" data-testid="hr-plan-generate"><Wand2 className="w-4 h-4" /> Generate from Skill Gaps</Button>
            {isAdmin && <Button size="sm" variant="ghost" onClick={runReminders} className="gap-1" data-testid="hr-plan-reminders"><Bell className="w-4 h-4" /> Run Reminders</Button>}
            <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={openNew} data-testid="hr-plan-add"><Plus className="w-4 h-4" /> Add Plan</Button>
          </div>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="hr-plan-summary">
          <Stat label="Total" value={summary.total} cls="bg-white border-slate-200 text-slate-700" />
          <Stat label="Completed" value={summary.completed} cls="bg-emerald-50 border-emerald-200 text-emerald-700" />
          <Stat label="Pending" value={summary.pending} cls="bg-amber-50 border-amber-200 text-amber-700" />
          <Stat label="Overdue" value={summary.overdue} cls="bg-red-50 border-red-200 text-red-700" />
          <Stat label="Completion" value={`${summary.completion_pct}%`} cls="bg-pink-50 border-pink-200 text-pink-700" />
        </div>
      )}

      <div className="flex items-center gap-2">
        <Label className="text-xs text-slate-500">Filter:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="hr-plan-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-slate-200 shadow-none"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/80">
            {canManage && <TableHead className="text-xs w-8"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="hr-plan-select-all" /></TableHead>}
            <TableHead className="text-xs">Employee</TableHead><TableHead className="text-xs">Skill</TableHead>
            <TableHead className="text-xs">Req.</TableHead><TableHead className="text-xs">Current</TableHead>
            <TableHead className="text-xs">Planned</TableHead><TableHead className="text-xs">Actual</TableHead>
            <TableHead className="text-xs">Status</TableHead><TableHead className="text-xs text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow> :
              rows.length === 0 ? <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center text-slate-400 py-8 text-sm">No plan entries. Use "Generate from Skill Gaps" or "Add Plan".</TableCell></TableRow> :
                rows.map(r => (
                  <TableRow key={r.id} className={r.status === 'overdue' ? 'bg-red-50/40' : ''} data-testid={`hr-plan-row-${r.id}`}>
                    {canManage && <TableCell>{r.status !== 'completed' && <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} data-testid={`hr-plan-select-${r.id}`} />}</TableCell>}
                    <TableCell className="text-sm font-medium">{r.employee_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.skill_name}{r.source === 'auto_gap' && <Badge variant="outline" className="text-[9px] ml-1">auto</Badge>}</TableCell>
                    <TableCell className="text-xs font-mono">{r.required_level || '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{r.current_level || '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{r.planned_date || '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{r.actual_date || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[r.status] || ''}`}>{r.status === 'overdue' && <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />}{r.status}</Badge></TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => navigate(`/hr/employees/${r.employee_id}`)}>Level <ArrowRight className="w-3 h-3" /></Button>
                      {canManage && r.status !== 'completed' && <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-pink-200 text-pink-700 hover:bg-pink-50" onClick={() => openSchedule(r)} data-testid={`hr-plan-schedule-${r.id}`}><CalendarPlus className="w-3 h-3" /> Schedule</Button>}
                      {canManage && r.status !== 'completed' && <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => complete(r)} data-testid={`hr-plan-complete-${r.id}`}><CheckCircle className="w-3 h-3" /> Complete</Button>}
                      {canManage && r.status === 'completed' && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => reopen(r)} data-testid={`hr-plan-reopen-${r.id}`}><RotateCcw className="w-3 h-3" /> Reopen</Button>}
                      {canManage && <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => remove(r)} data-testid={`hr-plan-delete-${r.id}`}><Trash2 className="w-3 h-3" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="hr-plan-dialog">
          <DialogHeader><DialogTitle>Add Training Plan</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Skill *</Label>
                <Select value={dlg.skill_id || undefined} onValueChange={v => setDlg({ ...dlg, skill_id: v })}>
                  <SelectTrigger data-testid="hr-plan-skill"><SelectValue placeholder="Select skill" /></SelectTrigger>
                  <SelectContent>{skills.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Planned Date *</Label><Input type="date" value={dlg.planned_date} onChange={e => setDlg({ ...dlg, planned_date: e.target.value })} data-testid="hr-plan-planned-date" /></div>
                <div><Label className="text-xs">Link Training (optional)</Label>
                  <Select value={dlg.training_id || '__none__'} onValueChange={v => setDlg({ ...dlg, training_id: v === '__none__' ? '' : v })}>
                    <SelectTrigger data-testid="hr-plan-training"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none__">None</SelectItem>{trainings.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Employees ({dlg.employee_ids.length}) — multiple = group training</Label>
                <div className="mt-1">
                  <BulkAttendeePicker emps={emps} fps={fps} selectedIds={dlg.employee_ids} onChange={(ids) => setDlg(d => ({ ...d, employee_ids: ids }))} testidPrefix="hr-plan-emp" />
                </div>
              </div>
              <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={dlg.notes} onChange={e => setDlg({ ...dlg, notes: e.target.value })} data-testid="hr-plan-notes" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-plan-save">{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sched} onOpenChange={(o) => !o && setSched(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="hr-schedule-dialog">
          <DialogHeader><DialogTitle>Schedule Training from Plan</DialogTitle></DialogHeader>
          {sched && (() => {
            const covering = sched.skill_id ? trainings.filter(t => (t.skills_covered || []).includes(sched.skill_id)) : [];
            const options = covering.length ? covering : trainings;
            return (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Skill: <b className="text-slate-700">{sched.skill_label}</b></p>
                <div><Label className="text-xs">Training *</Label>
                  <Select value={sched.training_id || undefined} onValueChange={v => setSched({ ...sched, training_id: v })}>
                    <SelectTrigger data-testid="hr-schedule-training"><SelectValue placeholder={covering.length ? 'Select training' : 'Select a training'} /></SelectTrigger>
                    <SelectContent>{options.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                  </Select>
                  {sched.skill_id && covering.length === 0 && <p className="text-[11px] text-amber-600 mt-1">No training master covers this skill. Consider adding the skill to a training's "skills covered".</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Planned Date *</Label><Input type="date" value={sched.planned_date} onChange={e => setSched({ ...sched, planned_date: e.target.value })} data-testid="hr-schedule-date" /></div>
                  <div><Label className="text-xs">Trainer (optional)</Label>
                    <Select value={sched.trainer_id || '__none__'} onValueChange={v => setSched({ ...sched, trainer_id: v === '__none__' ? '' : v })}>
                      <SelectTrigger data-testid="hr-schedule-trainer"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent><SelectItem value="__none__">None</SelectItem>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Attendees ({sched.employee_ids.length}) — carried over from plan, add more for a group</Label>
                  <div className="mt-1">
                    <BulkAttendeePicker emps={emps} fps={fps} selectedIds={sched.employee_ids} onChange={(ids) => setSched(s => ({ ...s, employee_ids: ids }))} testidPrefix="hr-schedule-emp" />
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter><Button variant="outline" onClick={() => setSched(null)}>Cancel</Button><Button onClick={saveSchedule} disabled={savingSched} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-schedule-save">{savingSched ? 'Scheduling...' : 'Schedule'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
